// Runtime configuration

// Global initial data configuration, used for initializing user info and permissions in the Layout
// For more information, see the documentation: https://umijs.org/docs/api/runtime-config#getinitialstate
import ErrorBoundary from '@/components/ErrorBoundary';
import OfflineNotice from '@/components/OfflineNotice';
import Logo from '@/layout/components/Logo';
import UserMenuFooter from '@/layout/components/UserMenuFooter';
import { normalizeMenuItems } from '@/layout/menu/utils';
import { createRequestConfig } from '@/layout/request/interceptors';
import { getCurrentUser } from '@/services/user/userService';
import type { User } from '@/types/user';
import {
  readRefreshTokenCookie,
  readStoredAuthTokens,
} from '@/utils/authTokens';
import { configureMonitoringSink } from '@/utils/monitoringSink';
import {
  persistSidebarCollapsed,
  readSidebarCollapsed,
} from '@/utils/sidebarStorage';
import { suppressKnownReactWarnings } from '@/utils/suppressKnownReactWarnings';
import {
  history,
  Link,
  type RequestConfig,
  type RunTimeLayoutConfig,
} from '@umijs/max';
import { Button, Result } from 'antd';
import React from 'react';

configureMonitoringSink();
suppressKnownReactWarnings();

export type CollapseType = 'clickTrigger' | 'responsive';

export type InitialState = {
  name: string;
  collapsed?: boolean;
  collapseType?: CollapseType;
  currentUser?: User;
};

export async function getInitialState(): Promise<InitialState> {
  const storedCollapsed = readSidebarCollapsed();
  const baseState: InitialState = {
    name: '',
    collapsed: storedCollapsed ?? false,
  };

  if (typeof window === 'undefined') {
    return baseState;
  }

  const hasAuthSession = Boolean(
    readStoredAuthTokens()?.token || readRefreshTokenCookie(),
  );

  if (!hasAuthSession) {
    return baseState;
  }

  try {
    const currentUser = await getCurrentUser();
    return { ...baseState, currentUser };
  } catch (error) {
    return baseState;
  }
}

const resolveLogoVariant = (logoDom: React.ReactNode): 'header' | 'sider' => {
  if (
    React.isValidElement(logoDom) &&
    (logoDom.props?.className ?? '').includes('ant-pro-global-header-logo')
  ) {
    return 'header';
  }
  return 'sider';
};

const wrapLogo = (
  logoDom: React.ReactNode,
  content: React.ReactNode,
): React.ReactNode => {
  if (React.isValidElement(logoDom)) {
    const { className, style } = logoDom.props ?? {};
    return (
      <div className={className} style={style}>
        {content}
      </div>
    );
  }
  return content;
};

export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => ({
  title: false,
  collapsed: initialState?.collapsed,
  breakpoint: false,
  menuHeaderRender: (
    logoDom: React.ReactNode,
    _title: React.ReactNode,
    props,
  ) => {
    const variant = resolveLogoVariant(logoDom);
    const logoNode = (
      <Logo
        collapsed={props?.collapsed ?? initialState?.collapsed}
        variant={variant}
      />
    );

    return wrapLogo(logoDom, logoNode);
  },
  onCollapse: (collapsed: boolean, type?: CollapseType) => {
    if (type === 'responsive') {
      return;
    }
    setInitialState?.((s: object) => ({
      ...s,
      collapsed,
      collapseType: type,
    }));
    persistSidebarCollapsed(collapsed);
  },
  menuDataRender: (menuItems) => normalizeMenuItems(menuItems),
  menuItemRender: (itemProps, defaultDom, menuProps) => {
    let content = defaultDom;
    if (
      !menuProps?.isMobile &&
      menuProps?.collapsed &&
      React.isValidElement(defaultDom)
    ) {
      const nextClassName = [defaultDom.props?.className, 'menu-item-icon-only']
        .filter(Boolean)
        .join(' ');
      content = React.cloneElement(defaultDom, { className: nextClassName });
    }

    const handleMenuClick = (
      event?: React.MouseEvent<
        HTMLDivElement | HTMLAnchorElement | HTMLSpanElement
      >,
    ) => {
      if (React.isValidElement(content)) {
        content.props?.onClick?.(event);
      }
      if (menuProps?.isMobile) {
        itemProps?.onClick?.();
      }
    };

    if (itemProps?.isUrl && itemProps?.itemPath) {
      return (
        <a
          href={itemProps.itemPath}
          target="_blank"
          rel="noreferrer"
          onClick={handleMenuClick}
        >
          {content}
        </a>
      );
    }

    if (itemProps?.itemPath) {
      return (
        <Link
          to={itemProps.itemPath}
          replace={itemProps?.replace}
          onClick={handleMenuClick}
        >
          {content}
        </Link>
      );
    }

    if (React.isValidElement(content)) {
      return React.cloneElement(content, {
        onClick: handleMenuClick,
      });
    }

    return content;
  },
  menuFooterRender: (menuProps) => (
    <UserMenuFooter mode={menuProps?.isMobile ? 'mobile' : 'desktop'} />
  ),
  childrenRender: (children) => (
    <ErrorBoundary>
      <OfflineNotice />
      {children}
    </ErrorBoundary>
  ),
  unAccessible: (
    <Result
      status="403"
      title="403"
      subTitle="Lo sentimos, no tenés permiso para acceder a esta página."
      extra={
        <Button type="primary" onClick={() => history.push('/')}>
          Volver al inicio
        </Button>
      }
    />
  ),
  noFound: (
    <Result
      status="404"
      title="404"
      subTitle="Lo sentimos, la página que visitaste no existe."
      extra={
        <Button type="primary" onClick={() => history.push('/')}>
          Volver al inicio
        </Button>
      }
    />
  ),
});

export const request: RequestConfig = createRequestConfig();
